import PageShell from "@/components/layout/PageShell";

/**
 * Reusable long-form legal page layout — matches the Carztohire brand system
 * (navy hero + serif h1 + editorial content column). Consumers pass a `sections`
 * array where each item is `{ h?, sh?, p?, list? }`:
 *   h    → h2 (section heading, numbered automatically)
 *   sh   → h3 (subheading, no auto-numbering)
 *   p    → paragraph or array of paragraphs
 *   list → array of bullet strings
 */
export default function PolicyPage({
  overline,
  title,
  intro,
  effectiveDate,
  sections = [],
  testId,
}) {
  let sectionNum = 0;
  return (
    <PageShell>
      {/* Hero */}
      <section
        className="bg-[#0B2545] text-white pt-16 md:pt-24 pb-12 md:pb-20"
        data-testid={testId ? `${testId}-hero` : undefined}
      >
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="overline-light mb-6">{overline}</div>
          <h1 className="font-serif text-[9vw] md:text-[6vw] lg:text-[5vw] leading-[1.02] tracking-[-0.02em] text-white max-w-5xl">
            {title}
          </h1>
          {intro && (
            <p className="mt-6 md:mt-8 text-white/85 text-base md:text-xl leading-relaxed max-w-3xl">
              {intro}
            </p>
          )}
          {effectiveDate && (
            <div className="mt-6 md:mt-8 overline-light opacity-75">
              Effective date · {effectiveDate}
            </div>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="py-12 md:py-24 bg-white" data-testid={testId ? `${testId}-body` : undefined}>
        <div className="mx-auto max-w-[900px] px-5 md:px-10">
          <div className="space-y-10 md:space-y-14">
            {sections.map((sec, i) => {
              const paragraphs = Array.isArray(sec.p) ? sec.p : sec.p ? [sec.p] : [];
              if (sec.h) sectionNum += 1;
              return (
                <div key={i} data-testid={testId ? `${testId}-section-${i}` : undefined}>
                  {sec.h && (
                    <div className="mb-4 md:mb-6">
                      <div className="overline mb-2 md:mb-3">
                        Section · {String(sectionNum).padStart(2, "0")}
                      </div>
                      <h2 className="font-serif text-2xl md:text-4xl leading-tight text-[#0B2545]">
                        {sec.h}
                      </h2>
                    </div>
                  )}
                  {sec.sh && (
                    <h3 className="font-serif text-xl md:text-2xl text-[#0B2545] mt-6 md:mt-8 mb-3">
                      {sec.sh}
                    </h3>
                  )}
                  {paragraphs.map((para, pi) => (
                    <p key={pi} className="text-[#3B4553] text-base md:text-lg leading-relaxed mt-3 first:mt-0">
                      {para}
                    </p>
                  ))}
                  {sec.list && sec.list.length > 0 && (
                    <ul className="mt-4 space-y-2">
                      {sec.list.map((item, li) => (
                        <li
                          key={li}
                          className="text-[#3B4553] text-base md:text-lg leading-relaxed pl-5 relative"
                        >
                          <span className="absolute left-0 top-[0.85em] h-1.5 w-1.5 bg-[#0B2545]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-16 md:mt-24 pt-8 border-t border-line">
            <p className="text-sm text-[#5B6472] leading-relaxed">
              Questions about this document? Reach us at{" "}
              <a href="mailto:info@carztohire.com" className="text-[#0B2545] underline underline-offset-2">
                info@carztohire.com
              </a>{" "}
              or call{" "}
              <a href="tel:+16026101396" className="text-[#0B2545] underline underline-offset-2">
                602 610 1396
              </a>
              .
            </p>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
